import { defineStore } from "pinia";
import camelcaseKeys from "camelcase-keys";
import axios from "axios";
import { ref, computed, watch } from "vue";
import { API } from "@/utils/api/api_paths";
import type { Eligibility } from "@/types/apiTypes";
import { useQuestionsStore } from "@/stores/questions";
import { axiosInstance } from "@/utils/api/axios_instance";
import { useMainStore } from "./main";
import { QuarterlySubscriptionVersion, useABVersionsStore } from "./abVersions";

// ! ----- This are values that should be kept in sync with Shopify -----
const ProductCategories = ["bioniq_go_light", "bioniq_go_subscription"] as const;
export type ProductCategory = (typeof ProductCategories)[number];

const GOProductsIds = {
  light: import.meta.env.VITE_SHOPIFY_PRODUCT_ID_LIGHT,
  monthlySubscription: import.meta.env.VITE_SHOPIFY_PRODUCT_ID_MONTH,
  quarterlySubscription: import.meta.env.VITE_SHOPIFY_PRODUCT_ID_QUART,
} as const;
type GOProductId = (typeof GOProductsIds)[keyof typeof GOProductsIds];
// ! ----- This are values that should be kept in sync with Shopify -----

interface ShopifyVariantNode {
  node: {
    id: string;
    title: string;
    contextualPricing: {
      compareAtPrice: null;
      price: {
        amount: string;
        currencyCode: string;
      };
    };
    product: {
      tags: ProductCategory[];
    };
  };
}

interface ShopifyProductNode {
  node: {
    handle: string;
    id: string;
    title: string;
    variants: {
      edges: ShopifyVariantNode[];
    };
  };
}

interface ShopifyProductsCollection {
  products: {
    edges: ShopifyProductNode[];
  };
}

export interface GOProduct {
  category: ProductCategory;
  id: GOProductId;
  name: string;
  price: string;
  currency: string;
}

const QuarterlyEligibleCountryCodes = ["SA", "KW", "QA", "BH", "OM"];

export const useProductsStore = defineStore("productsStore", () => {
  const mainStore = useMainStore();

  const eligibility = ref<Eligibility | null>(null);

  const shopifyGOCollection = ref<ShopifyProductsCollection | null>(null);
  const selectedProductId = ref<GOProductId>(GOProductsIds.monthlySubscription);

  const is1MonthEligible = computed<boolean>(() => {
    if (!eligibility.value) return true;
    return eligibility.value.maxOrdersContext.allowed;
  });

  const currency = computed<string>(() => {
    return selectedProduct.value.currency;
  });

  const GOProducts = computed<GOProduct[]>(() => {
    if (!shopifyGOCollection.value) return [];
    const products: GOProduct[] = [];
    shopifyGOCollection.value.products.edges.forEach((productEdge) => {
      productEdge.node.variants.edges.forEach((variant) => {
        const product: any = {};
        for (const category of ProductCategories) {
          if ((variant.node.product.tags as string[]).includes(category)) {
            product.category = category;
            break;
          }
        }
        product.id = variant.node.id;
        product.price = variant.node.contextualPricing.price.amount;
        product.name = variant.node.title;
        product.currency = variant.node.contextualPricing.price.currencyCode;
        products.push(product);
      });
    });
    return products;
  });

  const selectedProduct = computed<GOProduct>(() => {
    return GOProducts.value.find((product) => product.id === selectedProductId.value)!;
  });

  const selectedProductCategory = computed<ProductCategory>(() => {
    return selectedProduct.value.category;
  });

  const GOLightProduct = computed<GOProduct>(() => {
    return GOProducts.value.find((product) => product.id === GOProductsIds.light)!;
  });

  const GOMonthlySubscriptionProduct = computed<GOProduct>(() => {
    return GOProducts.value.find((product) => product.id === GOProductsIds.monthlySubscription)!;
  });

  const GOQuarterlySubscriptionProduct = computed<GOProduct>(() => {
    return GOProducts.value.find((product) => product.id === GOProductsIds.quarterlySubscription)!;
  });

  const GOSubscriptionProduct = computed<GOProduct>(() => {
    return isQuarterlyPaymentType.value ? GOQuarterlySubscriptionProduct.value : GOMonthlySubscriptionProduct.value;
  });

  const isQuarterlyPaymentType = computed<boolean>(() => {
    const abVersionsStore = useABVersionsStore();
    const isQuarterlyVersion =
      abVersionsStore.quarterlySubscriptionABVersion === QuarterlySubscriptionVersion.B &&
      QuarterlyEligibleCountryCodes.includes(mainStore.ipLocationCountryCode);
    return isQuarterlyVersion;
  });

  const quarterlyOrMonthlyPaymentType = computed<string>(() => {
    return isQuarterlyPaymentType.value ? "3m" : "1m";
  });

  const setSelectedProductId = (id: GOProductId) => {
    selectedProductId.value = id;
  };

  watch(shopifyGOCollection, () => {
    const productId = isQuarterlyPaymentType.value
      ? GOProductsIds.quarterlySubscription
      : GOProductsIds.monthlySubscription;
    setSelectedProductId(productId);
  });

  const fetchCheckEligibility = async () => {
    const questionsStore = useQuestionsStore();
    const url = encodeURI(API.CHECK_ELIGIBILITY.replace("{EMAIL}", questionsStore.lifestyleStore.email));
    try {
      const response = await axiosInstance.get(url);
      eligibility.value = camelcaseKeys(response.data);
    } catch (error) {
      console.error("Error while trying to fetch the eligibility of the 1 Month Product", error);
    }
  };

  const fetchShopifyGOCollection = async (): Promise<boolean> => {
    try {
      const res = await axios.get(API.SHOPIFY_PRODUCTS.replace("{CC}", mainStore.ipLocationCountryCode));
      shopifyGOCollection.value = camelcaseKeys(res.data.collection, { deep: true });
      return false;
    } catch (error) {
      console.error(`Error trying to fetch available products, ${error}`);
      const error2 = await fetchShopifyGOCollectionForGB();
      return error2;
    }
  };

  const fetchShopifyGOCollectionForGB = async (): Promise<boolean> => {
    try {
      const res = await axios.get(API.SHOPIFY_PRODUCTS.replace("{CC}", "GB"));
      shopifyGOCollection.value = camelcaseKeys(res.data.collection, { deep: true });
      return false;
    } catch (error) {
      console.error(`Error trying to fetch GB available products, ${error}`);
      return true;
    }
  };

  const isProductSelected = (id: GOProductId) => {
    return id === selectedProductId.value;
  };

  return {
    GOProducts,
    currency,
    selectedProductCategory,
    selectedProduct,
    GOLightProduct,
    GOMonthlySubscriptionProduct,
    GOQuarterlySubscriptionProduct,
    is1MonthEligible,
    setSelectedProductId,
    fetchShopifyGOCollection,
    fetchCheckEligibility,
    isProductSelected,
    GOSubscriptionProduct,
    quarterlyOrMonthlyPaymentType,
    isQuarterlyPaymentType,
  };
});
